import { useEffect, useRef } from 'react'

/**
 * useScrollReveal — IntersectionObserver-based scroll reveal for landing page.
 *
 * Attach to a section container. Children with `.reveal` class get `visible`
 * added when they enter the viewport (threshold 0.15, rootMargin -40px).
 * Respects `prefers-reduced-motion` — skips observer, shows immediately.
 */
export function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = ref.current
    if (!container) return

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const reveals = container.querySelectorAll('.reveal')

    if (prefersReduced) {
      reveals.forEach((el) => el.classList.add('visible'))
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    )

    reveals.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return ref
}
